import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { COMPANY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Your name", type: "text", placeholder: "Jane Doe" },
  { name: "email", label: "Email", type: "email", placeholder: "jane@company.com" },
  { name: "company", label: "Company", type: "text", placeholder: "Acme Corp" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative border-t border-white/10 bg-[#050505] py-24 md:py-40"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          {/* Left: heading + info */}
          <div className="lg:col-span-6">
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#0033ff]">
                07 — Contact
              </span>
              <h2 className="mt-6 font-display text-6xl font-extrabold uppercase leading-[0.85] tracking-tighter text-[#f4f4f5] md:text-8xl">
                Let&apos;s
                <br />
                talk.
              </h2>
            </Reveal>

            <div className="mt-14 space-y-8 border-t border-white/10 pt-8">
              {[
                { k: "Email", v: COMPANY.email, href: `mailto:${COMPANY.email}` },
                { k: "Phone", v: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
                { k: "Studio", v: COMPANY.address, href: null },
              ].map((row) => (
                <div key={row.k}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525b]">
                    {row.k}
                  </div>
                  {row.href ? (
                    <a
                      href={row.href}
                      data-testid={`contact-${row.k.toLowerCase()}`}
                      className="mt-2 block max-w-md font-mono text-sm text-[#f4f4f5] transition-colors hover:text-[#0033ff]"
                    >
                      {row.v}
                    </a>
                  ) : (
                    <p className="mt-2 max-w-md font-mono text-sm text-[#f4f4f5]">{row.v}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                {FIELDS.map((f) => (
                  <div key={f.name}>
                    <label
                      htmlFor={f.name}
                      className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525b]"
                    >
                      {f.label}
                    </label>
                    <input
                      id={f.name}
                      type={f.type}
                      value={form[f.name]}
                      onChange={update(f.name)}
                      placeholder={f.placeholder}
                      data-testid={`contact-input-${f.name}`}
                      className="w-full border-0 border-b border-white/20 bg-transparent pb-3 font-mono text-base text-[#f4f4f5] outline-none transition-colors placeholder:text-[#3f3f46] focus:border-[#0033ff]"
                    />
                  </div>
                ))}
                <div>
                  <label
                    htmlFor="message"
                    className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525b]"
                  >
                    Project details
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your integration challenge…"
                    data-testid="contact-input-message"
                    className="w-full resize-none border-0 border-b border-white/20 bg-transparent pb-3 font-mono text-base text-[#f4f4f5] outline-none transition-colors placeholder:text-[#3f3f46] focus:border-[#0033ff]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group flex w-full items-center justify-between border border-white/20 px-6 py-5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-[#0033ff] hover:bg-[#0033ff] disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send message"}
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <ArrowUpRight
                      size={16}
                      className="transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
                    />
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
